import { useQuery } from "@tanstack/react-query";
import { type VariantProps } from "tailwind-variants";

import { useRef, useState } from "react";

import { useUser } from "@/contexts/user-provider";

import { useProviders } from "@/hooks/use-providers";

import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";

import { providerQueryOptions } from "@/api/provider/admin/provider-query";

const badgeVariants = tv({
  base: [
    "flex shrink-0 items-center justify-center rounded-full text-white font-medium",
  ],
  variants: {
    variant: {
      primary: ["bg-primary"],
      secondary: [" border text-foreground-secondary"],
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

interface ProviderAvatarProps extends VariantProps<typeof badgeVariants> {
  providerId: string;
  size?: number;
  fontSize?: number;
  variant?: "primary" | "secondary";
  backgroundColor?: string;
  textColor?: string;
  fontWeight?: string;
  className?: string;
}

export default function ProviderAvatar({
  providerId,
  size = 40,
  fontSize,
  variant,
  backgroundColor,
  textColor,
  fontWeight,
  className,
}: ProviderAvatarProps) {
  const { currentUser } = useUser();
  const { providerIdProviderMap } = useProviders();
  const provider = providerIdProviderMap.get(providerId);

  // Fall back to current user if provider not in list
  const fallbackUser =
    !provider && currentUser?.id === providerId ? currentUser : null;
  const effectiveProvider = provider || fallbackUser;

  const profileImage = effectiveProvider?.profileImage;
  // Use profileImageUrl directly if available (already a signed URL from the backend)
  const preSignedUrl = effectiveProvider?.profileImageUrl;

  const { data: signedUrl, refetch } = useQuery({
    ...providerQueryOptions.profileImageSignedUrl(profileImage || ""),
    enabled:
      !!profileImage && profileImage.startsWith("gs://") && !preSignedUrl,
  });

  const hasTriedRefetchRef = useRef(false);
  // A photo that won't load shows the browser's broken-image icon and alt text
  // crammed into the circle, so show the initials instead.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const displayUrl =
    preSignedUrl ||
    signedUrl ||
    (profileImage && !profileImage.startsWith("gs://") ? profileImage : null);

  const userInitials = `${effectiveProvider?.firstName?.charAt(0).toUpperCase() || ""}${effectiveProvider?.lastName?.charAt(0).toUpperCase() || ""}`;
  const thisFontSize = fontSize ?? Math.floor(size * 0.4);

  const handleImageError = () => {
    setFailedUrl(displayUrl);
    if (profileImage?.startsWith("gs://") && !hasTriedRefetchRef.current) {
      hasTriedRefetchRef.current = true;
      refetch();
    }
  };

  if (displayUrl && displayUrl !== failedUrl) {
    return (
      <img
        src={displayUrl}
        alt={userInitials || "Profile"}
        className={cx("rounded-full object-cover", className)}
        style={{
          height: `${size}px`,
          width: `${size}px`,
        }}
        onError={handleImageError}
      />
    );
  }

  return (
    <div
      className={cx(badgeVariants({ variant }), className)}
      style={{
        fontSize: `${thisFontSize}px`,
        height: `${size}px`,
        width: `${size}px`,
        backgroundColor: `${backgroundColor}`,
        color: `${textColor}`,
        fontWeight: `${fontWeight}`,
      }}
    >
      {userInitials}
    </div>
  );
}
